import { useEffect, useRef, useState } from 'react';
import { Camera, LoaderCircle } from 'lucide-react';
import { Button, Description, useOverlayState } from '@heroui/react';
import { ImageFilePicker } from '../../../components/ui/image-file-picker';
import { UserAvatar } from '../../../components/ui/user-avatar';
import { isLikelyImageFile } from '../../../lib/image-file';
import { uploadProfilePhoto } from '../../../lib/firebase-storage';
import { showErrorFromUnknown, showError } from '../../../lib/toast';
import { ProfilePhotoCropDialog } from './profile-photo-crop-dialog';

const MAX_BYTES = 5 * 1024 * 1024;

interface ProfilePhotoFieldProps {
  name: string;
  photoUrl?: string | null;
  onChanged: (photoUrl: string) => Promise<void>;
}

export function ProfilePhotoField({ name, photoUrl, onChanged }: ProfilePhotoFieldProps) {
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const buttonInputRef = useRef<HTMLInputElement>(null);
  const cropDialog = useOverlayState();
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [sourceUrl, setSourceUrl] = useState<string | null>(null);
  const displayUrl = previewUrl ?? photoUrl ?? null;

  const resetFileInputs = () => {
    if (avatarInputRef.current) avatarInputRef.current.value = '';
    if (buttonInputRef.current) buttonInputRef.current.value = '';
  };

  const clearSource = () => {
    if (sourceUrl) URL.revokeObjectURL(sourceUrl);
    setSourceUrl(null);
    resetFileInputs();
  };

  const handleFile = (file: File | null) => {
    if (!file || uploading) return;
    if (!isLikelyImageFile(file)) {
      showError('Envie um arquivo de imagem.');
      return;
    }
    if (file.size > MAX_BYTES) {
      showError('A foto deve ter no máximo 5 MB.');
      return;
    }

    if (sourceUrl) URL.revokeObjectURL(sourceUrl);
    const nextUrl = URL.createObjectURL(file);
    setSourceUrl(nextUrl);
    cropDialog.open();
  };

  const handleCropCancel = () => {
    cropDialog.close();
    clearSource();
  };

  useEffect(() => {
    if (cropDialog.isOpen || uploading || !sourceUrl) return;
    URL.revokeObjectURL(sourceUrl);
    setSourceUrl(null);
    resetFileInputs();
  }, [cropDialog.isOpen, uploading, sourceUrl]);

  const handleCropConfirm = async (file: File) => {
    const localUrl = URL.createObjectURL(file);
    setPreviewUrl(localUrl);
    setUploading(true);
    try {
      const uploadedUrl = await uploadProfilePhoto(file);
      await onChanged(uploadedUrl);
      cropDialog.close();
      clearSource();
    } catch (error) {
      showErrorFromUnknown(error, 'Não foi possível atualizar a foto.');
      throw error;
    } finally {
      setPreviewUrl(null);
      URL.revokeObjectURL(localUrl);
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-5">
      <ImageFilePicker
        inputRef={avatarInputRef}
        disabled={uploading}
        aria-label="Alterar foto de perfil"
        className="shrink-0 rounded-full"
        onFile={handleFile}
      >
        <span className="relative block size-24">
          <UserAvatar name={name} photoUrl={displayUrl} size="lg" className="size-24 text-xl" />
          <span className="absolute bottom-0 right-0 flex size-8 items-center justify-center rounded-full border-2 border-surface bg-accent text-white">
            {uploading ? <LoaderCircle size={14} className="animate-spin" /> : <Camera size={14} />}
          </span>
          {uploading ? (
            <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 text-white">
              <LoaderCircle size={22} className="animate-spin" />
            </span>
          ) : null}
        </span>
      </ImageFilePicker>

      <div className="flex min-w-0 flex-col gap-2">
        <Description>
          Esta foto aparece no menu, no ranking e para os outros moradores. Você pode recortar e
          ajustar o enquadramento.
        </Description>
        <ImageFilePicker inputRef={buttonInputRef} disabled={uploading} onFile={handleFile}>
          <Button type="button" variant="secondary" size="sm" isDisabled={uploading} tabIndex={-1}>
            <Camera size={16} />
            {displayUrl ? 'Alterar foto' : 'Adicionar foto'}
          </Button>
        </ImageFilePicker>
      </div>

      <ProfilePhotoCropDialog
        state={cropDialog}
        imageSrc={sourceUrl}
        onConfirm={handleCropConfirm}
        onCancel={handleCropCancel}
      />
    </div>
  );
}
